import {
  Choice,
  ChoiceType,
  CopilotContext,
  Message,
  MessageType,
  RateType,
} from "../../../../apis/copilot/types";
import { rateMessage, sendMessage } from "../../../../apis/copilot/copilot";
import { useWorkspaceStore } from "../../../../store/workspace/WorkspaceStore";
import { useCopilotStore } from "../../../../store/copilot/CopilotStore";
import { Workspace } from "../../../../types/workspace";
import { MultiSelect } from "./MultiSelect";
import { SingleSelect } from "./SingleSelect";
import { TreeMultiSelect } from "./TreeMultiSelect";
import { Link, Stack, Typography } from "@mui/material";
import { totalExternalItSpendCategory } from "../../../../utils/consts";
import { useEffect, useRef, useState } from "react";
import { useCopilotContext } from "../../../../hooks/useCopilotContext";
import { mergeMessages } from "../../utils";
import {
  TAB_TO_INDEX,
  useConfigTabNavigation,
} from "../../../../pages/Workspace/WorkspaceConfiguration/hooks/useConfigTabNavigation";
import { ConfigTab } from "../../../../pages/Workspace/types";

interface Props {
  messageId: string;
  choiceType: ChoiceType;
  choices: Choice[];
}

export const Select: React.FC<Props> = ({ messageId, choiceType, choices }) => {
  const { setDraft } = useWorkspaceStore();
  const { threadId, setIsLoading, setMessages, highlightElement } =
    useCopilotStore();
  const context = useCopilotContext();
  const { activeTabIndex, handleTabChange } = useConfigTabNavigation();

  const [isWaitingForContextUpdate, setIsWaitingForContextUpdate] =
    useState(false);
  const initialContext = useRef(context);

  useEffect(() => {
    const sendInsertionConfirmationMsg = async () => {
      setIsLoading(true);

      const answers = await sendMessage(
        threadId as string,
        "I inserted it.",
        context as CopilotContext,
        (answers: Message[]) => {
          setMessages((currentMessages) =>
            mergeMessages(currentMessages, answers)
          );
        },
        () => {
          setIsLoading(false);
        }
      );

      setMessages((current) => [...current, ...answers]);
    };

    if (
      isWaitingForContextUpdate &&
      context &&
      JSON.stringify(context) !== JSON.stringify(initialContext.current)
    ) {
      sendInsertionConfirmationMsg();
      setIsWaitingForContextUpdate(false);
    }
  }, [isWaitingForContextUpdate, context, setIsLoading, setMessages, threadId]);

  const pushUpdateInfoMessage = () => {
    const choiceTypeToConfigAttribute = {
      [ChoiceType.IT_SPEND_FILTER]: "spend categories",
      [ChoiceType.INDUSTRY_FILTER]: "industries",
      [ChoiceType.GEOGRAPHY_FILTER]: "countries",
      [ChoiceType.EMPLOYEE_RANGE_FILTER]: "employee range",
      [ChoiceType.REVENUE_RANGE_FILTER]: "revenue range",
      [ChoiceType.COMPETITIVE_ANALYSIS]: "competitors",
    };

    const configTab =
      choiceType === ChoiceType.COMPETITIVE_ANALYSIS
        ? "Competitors"
        : "Market Sizing";

    const configTabIndex =
      choiceType === ChoiceType.COMPETITIVE_ANALYSIS
        ? TAB_TO_INDEX[ConfigTab.COMPETITORS]
        : TAB_TO_INDEX[ConfigTab.MARKET_SIZING];

    setMessages((currentMessages) => [
      ...currentMessages,
      {
        id: Math.random().toString(),
        type: MessageType.UI_MESSAGE,
        payload: {
          content: "",
          react_content: (
            <Stack direction="row" gap="3px">
              <Typography>{`I've updated the ${choiceTypeToConfigAttribute[choiceType]} on`}</Typography>
              <Link
                underline="none"
                component="button"
                onClick={() => {
                  handleTabChange(configTabIndex);
                }}
              >
                {configTab}
              </Link>
              <Typography>.</Typography>
            </Stack>
          ),
          timestamp: new Date(),
        },
        rate: null,
      },
    ]);
  };

  const insertIntoSpendCategories = (ids: string[]) => {
    setDraft((draft) => {
      const currentWorkspace = draft as Workspace;

      const prevIds =
        currentWorkspace.configuration.tam?.spendCategoryIds || [];

      const prevWeights =
        currentWorkspace.configuration.tam?.spendCategoryWeights || [];

      const mergedIds = Array.from(
        new Set([
          ...(prevIds[0] === totalExternalItSpendCategory ? [] : prevIds),
          ...ids,
        ])
      );

      const existingWeightIds = prevWeights.map((w) => w.id);

      const newWeights = ids
        .filter((id) => !existingWeightIds.includes(id))
        .map((id) => ({ id, weight: 100 }));

      const mergedWeights = [
        ...(prevIds[0] === totalExternalItSpendCategory ? [] : prevWeights),
        ...newWeights,
      ];

      return {
        ...currentWorkspace,
        configuration: {
          ...currentWorkspace.configuration,
          tam: {
            ...currentWorkspace.configuration.tam,
            spendCategoryIds: mergedIds,
            spendCategoryWeights: mergedWeights,
          },
        },
      };
    });

    highlightElement("spend_category_chip");
    setTimeout(() => highlightElement(null), 3000);

    if (activeTabIndex !== TAB_TO_INDEX[ConfigTab.MARKET_SIZING])
      pushUpdateInfoMessage();

    const rateValue = ids.length / choices.length;
    rateMessage(threadId as string, messageId, RateType.PERCENTAGE, rateValue);

    setIsWaitingForContextUpdate(true);
  };

  const insertIntoIndustries = (ids: string[]) => {
    setDraft((draft) => {
      const currentWorkspace = draft as Workspace;
      const prevIds =
        currentWorkspace.configuration.sam?.industry?.ids?.map(Number) || [];
      const mergedIds = Array.from(new Set([...prevIds, ...ids.map(Number)]));
      return {
        ...currentWorkspace,
        configuration: {
          ...currentWorkspace.configuration,
          sam: {
            ...currentWorkspace.configuration.sam,
            industry: {
              ...currentWorkspace.configuration.sam.industry,
              ids: mergedIds,
              sicIds: [],
              naicsIds: [],
            },
          },
        },
      };
    });

    highlightElement("industry_chip");
    setTimeout(() => highlightElement(null), 3000);

    if (activeTabIndex !== TAB_TO_INDEX[ConfigTab.MARKET_SIZING])
      pushUpdateInfoMessage();

    const rateValue = ids.length / choices.length;
    rateMessage(threadId as string, messageId, RateType.PERCENTAGE, rateValue);

    setIsWaitingForContextUpdate(true);
  };

  const insertIntoCountries = (ids: string[]) => {
    setDraft((draft) => {
      const currentWorkspace = draft as Workspace;
      const prevIds =
        currentWorkspace.configuration.sam?.geography?.countryIds || [];
      const mergedIds = Array.from(new Set([...prevIds, ...ids]));
      return {
        ...currentWorkspace,
        configuration: {
          ...currentWorkspace.configuration,
          sam: {
            ...currentWorkspace.configuration.sam,
            geography: {
              ...currentWorkspace.configuration.sam.geography,
              countryIds: mergedIds,
              regionsIds: [],
              stateIds: [],
            },
          },
        },
      };
    });

    highlightElement("geography_chip");
    setTimeout(() => highlightElement(null), 3000);

    if (activeTabIndex !== TAB_TO_INDEX[ConfigTab.MARKET_SIZING])
      pushUpdateInfoMessage();

    const rateValue = ids.length / choices.length;
    rateMessage(threadId as string, messageId, RateType.PERCENTAGE, rateValue);

    setIsWaitingForContextUpdate(true);
  };

  const insertIntoEmployeeRange = (selectedChoice: Choice) => {
    setDraft((draft) => {
      const currentWorkspace = draft as Workspace;
      return {
        ...currentWorkspace,
        configuration: {
          ...currentWorkspace.configuration,
          som: {
            ...currentWorkspace.configuration.som,
            employeeRange: {
              min: selectedChoice?.min as number,
              max: selectedChoice?.max as number,
            },
          },
        },
      };
    });

    highlightElement("employee_chip");
    setTimeout(() => highlightElement(null), 3000);

    if (activeTabIndex !== TAB_TO_INDEX[ConfigTab.MARKET_SIZING])
      pushUpdateInfoMessage();

    const rateValue = 1;
    rateMessage(threadId as string, messageId, RateType.PERCENTAGE, rateValue);

    setIsWaitingForContextUpdate(true);
  };

  const insertIntoRevenueRange = (selectedChoice: Choice) => {
    setDraft((draft) => {
      const currentWorkspace = draft as Workspace;
      return {
        ...currentWorkspace,
        configuration: {
          ...currentWorkspace.configuration,
          som: {
            ...currentWorkspace.configuration.som,
            revenueRange: {
              min: selectedChoice?.min as number,
              max: selectedChoice?.max as number,
            },
          },
        },
      };
    });

    highlightElement("revenue_chip");
    setTimeout(() => highlightElement(null), 3000);

    if (activeTabIndex !== TAB_TO_INDEX[ConfigTab.MARKET_SIZING])
      pushUpdateInfoMessage();

    const rateValue = 1;
    rateMessage(threadId as string, messageId, RateType.PERCENTAGE, rateValue);

    setIsWaitingForContextUpdate(true);
  };

  const insertIntoCompetitors = (ids: string[]) => {
    const competitorsProductIds = ids.map(Number);

    setDraft((draft) => {
      const currentWorkspace = draft as Workspace;
      const prevIds =
        currentWorkspace.configuration.competitorsProductIds || [];
      const mergedIds = Array.from(
        new Set([...prevIds, ...competitorsProductIds])
      );
      return {
        ...currentWorkspace,
        configuration: {
          ...currentWorkspace.configuration,
          competitorsProductIds: mergedIds,
        },
      };
    });

    highlightElement("competitor_input");
    setTimeout(() => highlightElement(null), 3000);

    if (activeTabIndex !== TAB_TO_INDEX[ConfigTab.COMPETITORS])
      pushUpdateInfoMessage();

    setIsWaitingForContextUpdate(true);
  };

  const renderSelect = () => {
    switch (choiceType) {
      case ChoiceType.IT_SPEND_FILTER:
        return (
          <TreeMultiSelect
            choices={choices}
            onInsert={insertIntoSpendCategories}
            target="TAM Definition"
          />
        );
      case ChoiceType.INDUSTRY_FILTER:
        return (
          <MultiSelect
            choices={choices}
            onInsert={insertIntoIndustries}
            target="SAM Definition"
          />
        );
      case ChoiceType.GEOGRAPHY_FILTER:
        return (
          <MultiSelect
            choices={choices}
            onInsert={insertIntoCountries}
            target="SAM Definition"
          />
        );
      case ChoiceType.EMPLOYEE_RANGE_FILTER:
        return (
          <SingleSelect
            choices={choices}
            onInsert={insertIntoEmployeeRange}
            target="SOM Definition"
          />
        );
      case ChoiceType.REVENUE_RANGE_FILTER:
        return (
          <SingleSelect
            choices={choices}
            onInsert={insertIntoRevenueRange}
            target="SOM Definition"
          />
        );
      case ChoiceType.COMPETITIVE_ANALYSIS:
        return (
          <MultiSelect
            choices={choices}
            onInsert={insertIntoCompetitors}
            target="Competitors Definition"
          />
        );
    }
  };

  return (
    <Stack
      borderRadius="30px"
      bgcolor="#FFFFFF"
      direction="column"
      padding="20px"
      gap="15px"
    >
      {renderSelect()}
    </Stack>
  );
};
